import { useEffect } from 'react';

/**
 * Hook to defer pixel/analytics script loading until after React hydration
 * using requestIdleCallback for optimal performance.
 *
 * This ensures pixels load outside the critical render path, improving
 * Time to Interactive (TTI) and Core Web Vitals scores.
 *
 * @param loadFn - Function that loads the pixel script, can optionally return cleanup function
 * @param deps - Dependencies array for the effect
 *
 * @example
 * usePixelLoader(() => {
 *   const script = document.createElement('script');
 *   script.src = 'https://analytics.example.com/pixel.js';
 *   document.head.appendChild(script);
 *
 *   // Return cleanup function to remove script on unmount
 *   return () => {
 *     if (script.parentNode) {
 *       script.parentNode.removeChild(script);
 *     }
 *   };
 * }, [pixelId]);
 */
export const usePixelLoader = (loadFn: () => void | (() => void), deps: React.DependencyList) => {
  useEffect(() => {
    let cleanup: (() => void) | void;

    // Defer pixel loading until browser is idle
    // This moves the load outside the critical render path
    const scheduleLoad = () => {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        // Use requestIdleCallback for optimal timing (loads during browser idle)
        window.requestIdleCallback(() => {
          try {
            cleanup = loadFn();
          } catch (error) {
            // Swallow pixel loading errors to prevent app crashes
            console.error('Error loading pixel:', error);
          }
        });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          try {
            cleanup = loadFn();
          } catch (error) {
            // Swallow pixel loading errors to prevent app crashes
            console.error('Error loading pixel:', error);
          }
        }, 100);
      }
    };

    scheduleLoad();

    // Call cleanup function on unmount if provided
    return () => {
      if (cleanup) {
        try {
          cleanup();
        } catch (error) {
          console.error('Error cleaning up pixel:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
